import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChefHat, Clock, AlertTriangle, CheckCircle, Play, Timer, RefreshCw, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

import { API_URL } from '@/config/api';

const priorityConfig = {
  1: { label: 'URGENT', color: 'bg-red-500', border: 'border-red-500', pulse: true },
  2: { label: 'NORMAL', color: 'bg-amber-500', border: 'border-amber-500', pulse: false },
  3: { label: 'LOW', color: 'bg-green-500', border: 'border-green-500', pulse: false }
};

const statusConfig = {
  paid: { label: 'New', color: 'bg-blue-500', action: 'Start Preparing', nextStatus: 'preparing' },
  preparing: { label: 'In Progress', color: 'bg-amber-500', action: 'Ready', nextStatus: 'ready_for_collection' }
};

export default function AdminKDS() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/kds/orders`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        
        // Check for new urgent orders
        if (soundEnabled) {
          const newUrgent = data.filter(o => o.priority === 1 && o.status === 'paid');
          if (newUrgent.length > orders.filter(o => o.priority === 1 && o.status === 'paid').length) {
            playAlert();
          }
        }
        
        setOrders(data);
      }
    } catch (error) {
      console.error('Error fetching KDS orders:', error);
    } finally {
      setLoading(false);
    }
  }, [token, soundEnabled, orders.length]);

  useEffect(() => {
    fetchOrders();
    
    if (autoRefresh) {
      const interval = setInterval(fetchOrders, 10000); // Refresh every 10 seconds
      return () => clearInterval(interval);
    }
  }, [fetchOrders, autoRefresh]);

  const playAlert = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleRQAQFi00NqJQwJGYofD1r1oKCQ1cqzX2pRZOysgbp3G0rF0VUAtgL/c5LeMW0xCbbXe5aNwPTBDaoOtt6BvT0R0q9Tj');
      audio.play();
    } catch (e) {
      // Audio not supported
      console.debug('Audio playback not supported');
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/kds/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`Order ${orderId.slice(-6)} updated!`);
        fetchOrders();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update order');
      }
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const getTimeSince = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMins = Math.floor((now - date) / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    return `${Math.floor(diffMins / 60)}h ${diffMins % 60}m ago`;
  };

  const newOrders = orders.filter(o => o.status === 'paid');
  const inProgress = orders.filter(o => o.status === 'preparing');

  return (
    <div className="min-h-screen bg-slate-900 p-4" data-testid="admin-kds">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ChefHat className="h-8 w-8 text-amber-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Kitchen Display</h1>
            <p className="text-slate-400 text-sm">Real-time order management</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={soundEnabled ? 'bg-green-500/20 text-green-400 border-green-500' : 'text-slate-400'}
          >
            {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchOrders}
            className="text-slate-400"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
          <Badge variant="outline" className="text-slate-400 border-slate-600">
            {orders.length} orders
          </Badge>
        </div>
      </div>

      {/* Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* New Orders Column */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse" />
            <h2 className="text-lg font-semibold text-white">New Orders</h2>
            <Badge className="bg-blue-500">{newOrders.length}</Badge>
          </div>
          <div className="space-y-4">
            <AnimatePresence>
              {newOrders.map((order, idx) => (
                <KDSOrderCard
                  key={order.order_id}
                  order={order}
                  onAction={() => updateStatus(order.order_id, 'preparing')}
                  actionLabel="Start Preparing"
                  delay={idx * 0.1}
                />
              ))}
            </AnimatePresence>
            {newOrders.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <CheckCircle className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>No new orders</p>
              </div>
            )}
          </div>
        </div>

        {/* In Progress Column */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-3 h-3 rounded-full bg-amber-500 animate-pulse" />
            <h2 className="text-lg font-semibold text-white">In Progress</h2>
            <Badge className="bg-amber-500">{inProgress.length}</Badge>
          </div>
          <div className="space-y-4">
            <AnimatePresence>
              {inProgress.map((order, idx) => (
                <KDSOrderCard
                  key={order.order_id}
                  order={order}
                  onAction={() => updateStatus(order.order_id, 'ready_for_collection')}
                  actionLabel="Mark Ready"
                  delay={idx * 0.1}
                />
              ))}
            </AnimatePresence>
            {inProgress.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Timer className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>No orders in progress</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function KDSOrderCard({ order, onAction, actionLabel, delay = 0 }) {
  const priority = priorityConfig[order.priority] || priorityConfig[2];
  
  const getTimeSince = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMins = Math.floor((now - date) / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    return `${Math.floor(diffMins / 60)}h ${diffMins % 60}m`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      transition={{ delay }}
      className={`bg-slate-800 rounded-xl border-l-4 ${priority.border} overflow-hidden`}
    >
      {/* Header */}
      <div className={`px-4 py-2 ${priority.color} flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <span className="font-mono font-bold text-white">
            #{order.order_id.slice(-6).toUpperCase()}
          </span>
          {order.priority === 1 && (
            <Badge className="bg-white/20 text-white animate-pulse">
              <AlertTriangle className="h-3 w-3 mr-1" />
              URGENT
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-white/80 text-sm">
          <Clock className="h-4 w-4" />
          {getTimeSince(order.created_at)}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-white font-medium">{order.customer_name}</span>
          <span className="text-slate-400 text-sm">
            {order.delivery_date} • {order.delivery_time || '12:00-13:00'}
          </span>
        </div>

        {/* Items */}
        <div className="space-y-2 mb-4">
          {order.items?.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between text-slate-300">
              <div className="flex items-center gap-2">
                <span className="bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded font-bold text-sm">
                  {item.quantity}x
                </span>
                <span>{item.name}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Special Instructions */}
        {order.special_instructions && (
          <div className="bg-slate-700/50 rounded-lg p-2 mb-4 text-sm text-amber-300">
            <strong>Note:</strong> {order.special_instructions}
          </div>
        )}

        {/* Action Button */}
        <Button
          onClick={onAction}
          className={`w-full ${
            order.status === 'paid' 
              ? 'bg-blue-500 hover:bg-blue-600' 
              : 'bg-green-500 hover:bg-green-600'
          }`}
          data-testid={`kds-action-${order.order_id}`}
        >
          <Play className="h-4 w-4 mr-2" />
          {actionLabel}
        </Button>
      </div>
    </motion.div>
  );
}
